/**
 * Session hors ligne : l'utilisateur resté connecté retrouve son espace sans réseau pendant
 * OFFLINE_MAX_DAYS jours après sa dernière connexion au serveur. Tout est effacé à la déconnexion.
 */
import { idb } from "./idb";

export const OFFLINE_MAX_DAYS = 3;
const OFFLINE_MAX_MS = OFFLINE_MAX_DAYS * 24 * 60 * 60 * 1000;
const SESSION_KEY = "session";

interface StoredSession<P> {
  userId: string;
  /** Dernière réponse du serveur (ms). */
  lastOnlineAt: number;
  /** Profil (`/auth/me`) pour rouvrir l'espace sans réseau. */
  profile: P;
}

/** Utilisateur dont les réponses sont gardées sur l'appareil (null : aucune mise en mémoire). */
let owner: string | null = null;
let lastTouch = 0;

export function getCacheOwner(): string | null {
  return owner;
}

/** Ouvre (ou change) la session hors ligne après une connexion réussie. */
export async function startOfflineSession<P>(userId: string, profile: P): Promise<void> {
  const stored = await idb.get<StoredSession<P>>("meta", SESSION_KEY);
  // Un autre utilisateur sur cet appareil : ses données mises en mémoire sont oubliées.
  if (stored && stored.userId !== userId) await idb.clear("cache");
  owner = userId;
  lastTouch = Date.now();
  await idb.set("meta", SESSION_KEY, { userId, lastOnlineAt: lastTouch, profile } satisfies StoredSession<P>);
}

/** Réponse du serveur reçue : repousse la limite de travail hors ligne (écrit au plus une fois par minute). */
export function touchOnline(): void {
  if (!owner || Date.now() - lastTouch < 60_000) return;
  lastTouch = Date.now();
  void idb.get<StoredSession<unknown>>("meta", SESSION_KEY).then((stored) => {
    if (stored && stored.userId === owner) void idb.set("meta", SESSION_KEY, { ...stored, lastOnlineAt: lastTouch });
  });
}

/** Profil de la session hors ligne encore valable, ou null (aucune, ou plus de OFFLINE_MAX_DAYS jours sans réseau). */
export async function resumeOfflineSession<P>(): Promise<P | null> {
  const stored = await idb.get<StoredSession<P>>("meta", SESSION_KEY);
  if (!stored || Date.now() - stored.lastOnlineAt > OFFLINE_MAX_MS) return null;
  owner = stored.userId;
  return stored.profile;
}

/** Utilisateur de la dernière session ouverte sur l'appareil (même expirée). */
export async function lastSessionUser(): Promise<string | null> {
  return (await idb.get<StoredSession<unknown>>("meta", SESSION_KEY))?.userId ?? null;
}

/** Déconnexion : efface les données de l'appareil (les actions d'autres utilisateurs restent en file). */
export async function endOfflineSession(): Promise<void> {
  const userId = owner;
  owner = null;
  await Promise.all([idb.clear("cache"), idb.delete("meta", SESSION_KEY)]);
  if (!userId) return;
  const items = await idb.all<{ id: string; userId: string }>("outbox");
  await Promise.all(items.filter((item) => item.userId === userId).map((item) => idb.delete("outbox", item.id)));
}
